import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeadManager from '@/components/base/HeadManager';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '香香漫画',
    url: 'https://xiangxiangmanga.com',
    description: siteInfo.description,
    email: siteInfo.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '广州',
      addressRegion: '广东',
      addressCountry: 'CN',
    },
    foundingDate: '2019',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于香香漫画',
    description: siteInfo.description,
    url: 'https://xiangxiangmanga.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '香香漫画',
      url: 'https://xiangxiangmanga.com',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9omst2ftmtnfcfh0a10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请重试';
        setFormError(errMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <HeadManager
        title="关于我们 - 香香漫画 | 品牌介绍"
        description="了解香香漫画品牌故事与发展历程。香香漫画自2019年创立，提供免费漫画在线阅读服务，拥有800万+用户和5000+漫画资源。查看用户口碑评价与联系方式。"
        keywords="香香漫画,关于我们,漫画平台,品牌介绍,联系方式,用户评价"
        canonical="https://xiangxiangmanga.com/about"
        scripts={[
          { type: 'application/ld+json', content: JSON.stringify(organizationSchema) },
          { type: 'application/ld+json', content: JSON.stringify(faqSchema) },
          { type: 'application/ld+json', content: JSON.stringify(aboutSchema) },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4">
              关于香香漫画
            </h1>
            <p className="text-foreground-600 mt-2 text-sm max-w-2xl">
              了解更多关于香香漫画的品牌故事、发展历程与核心价值
            </p>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed text-sm">
                  <p>
                    <strong>香香漫画</strong>创立于{siteInfo.founded}，是国内领先的免费漫画在线阅读平台。我们致力于为广大漫画爱好者提供最优质、最便捷的漫画阅读服务，让每一位读者都能轻松享受漫画带来的乐趣。
                  </p>
                  <p>
                    平台拥有超过<strong className="text-primary-500">{siteInfo.totalComics}</strong>部漫画资源，涵盖<strong>热血、恋爱、奇幻、搞笑、悬疑、科幻、校园、武侠</strong>等多个分类，满足不同读者的阅读需求。无论您喜欢什么类型的漫画，都能在香香漫画找到心仪的作品。
                  </p>
                  <p>
                    香香漫画始终坚持以用户为中心的产品理念，首创<strong className="text-primary-500">下拉式阅读</strong>体验，让追更变得更加流畅高效。同时，所有漫画资源均<strong className="text-primary-500">免费阅读</strong>，无需任何付费即可畅享完整内容。
                  </p>
                  <p>
                    截至目前，香香漫画已累计服务超过<strong className="text-primary-500">{siteInfo.totalUsers}</strong>读者，日均活跃用户超过<strong className="text-primary-500">{siteInfo.dailyActive}</strong>。我们深知每一份信任都来之不易，因此始终坚守初心，不断优化产品体验，丰富内容资源。
                  </p>
                </div>
              </div>

              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20illustration%20of%20a%20person%20reading%20manga%20on%20a%20tablet%20in%20a%20comfortable%20reading%20nook%20with%20bookshelves%20filled%20with%20colorful%20manga%20volumes%20soft%20natural%20lighting%20through%20window%20peaceful%20atmosphere%20warm%20color%20palette%20illustration%20style&width=1200&height=800&seq=about-brand&orientation=landscape"
                  alt="香香漫画 - 舒适的漫画阅读体验"
                  title="香香漫画品牌形象"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-14">
              <div className="text-center p-6 rounded-lg bg-background-100">
                <p className="font-heading text-3xl text-primary-500">{siteInfo.founded}</p>
                <p className="text-sm text-foreground-600 mt-1">创立年份</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <p className="font-heading text-3xl text-accent-500">{siteInfo.totalComics}</p>
                <p className="text-sm text-foreground-600 mt-1">漫画资源</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <p className="font-heading text-3xl text-primary-500">{siteInfo.totalUsers}</p>
                <p className="text-sm text-foreground-600 mt-1">注册用户</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <p className="font-heading text-3xl text-accent-500">{siteInfo.dailyActive}</p>
                <p className="text-sm text-foreground-600 mt-1">日均活跃</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              听听读者们怎么说
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }, (_, j) => (
                          <i key={j} className="ri-star-fill text-xs text-accent-500 w-3 h-3 flex items-center justify-center"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              常见问题
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              关于香香漫画的常见问题解答
            </p>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-lg overflow-hidden">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              有任何问题或建议，欢迎联系我们
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-4xl mx-auto">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-lg text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">微信公众号</p>
                    <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">公司地址</p>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-qq-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">QQ群</p>
                    <p className="text-sm text-foreground-600">{siteInfo.qq}</p>
                  </div>
                </div>
              </div>

              <div>
                <form id="contact-form" data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {formState === 'loading' ? '提交中...' : '发送留言'}
                  </button>

                  {formState === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formState === 'error' && formError && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}